import React from "react";
import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";
import StandardPDFLayout from "./StandardPDFLayout";
import type { MedicalRecord } from "../types";

interface MedicalRecordPDFProps {
  record: MedicalRecord;
  patientPhone?: string;
  doctorFullName?: string;
}

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: 'Helvetica',
    backgroundColor: '#ffffff',
  },
  container: { flex: 1, flexDirection: "column", justifyContent: "space-between" },
  section: { marginBottom: 12 },
  sectionTitle: { fontSize: 11, fontWeight: "bold", color: "#113285", marginBottom: 6, borderBottomWidth: 1, borderBottomColor: "#e5e7eb", borderBottomStyle: "solid", paddingBottom: 2 },
  row: { flexDirection: "row", justifyContent: "space-between", marginBottom: 3 },
  label: { fontSize: 9, color: "#6b7280", fontWeight: "bold", width: "30%" },
  value: { fontSize: 9, color: "#111827", width: "70%", textAlign: "right" },
  box: { marginBottom: 8, padding: 8, border: "1 solid #e5e7eb", borderRadius: 4 },
  boxTitle: { fontSize: 10, fontWeight: "bold", color: "#113285", marginBottom: 4 },
  small: { fontSize: 8, color: "#6b7280", marginTop: 2 },
});

const formatMedicalConditions = (conditions: string): string => {
  if (!conditions) return "";
  
  const fieldToLabel: { [key: string]: string } = {
    "hypertensionArterielle": "Hypertension artérielle",
    "maladieCardiaque": "Maladie cardiaque",
    "cholesterolEleve": "Cholestérol élevé",
    "avc": "AVC",
    "diabete": "Diabète",
    "maladieThyroidienne": "Maladie thyroïdienne",
    "drepanocytose": "Drépanocytose",
    "neurologique": "Problèmes neurologiques",
    "toux": "Toux",
    "douleursMammaires": "Douleurs mammaires",
    "anomalieUterine": "Anomalie utérine",
    "respirationSifflante": "Respiration sifflante",
    "maladiesInfectieuses": "Maladies infectieuses",
    "tuberculose": "Tuberculose",
    "asthme": "Asthme",
    "arthrite": "Arthrite",
    "cancer": "Cancer",
    "anemie": "Anémie",
    "defautGenetique": "Défaut génétique",
    "sinusite": "Sinusite",
    "vertige": "Vertige",
    "problemesMentaux": "Problèmes mentaux",
    "allergie": "Allergie",
    "chirurgieGyneco": "Chirurgie gynécologique",
    "infertilite": "Infertilité",
    "vih": "VIH",
    "cyclesIrreguliers": "Cycles irréguliers",
    "fatigue": "Fatigue"
  };

  return conditions
    .split(',')
    .map(field => field.trim())
    .filter(field => field)
    .map(field => fieldToLabel[field] || field)
    .join(', ');
};

const MedicalRecordContent: React.FC<MedicalRecordPDFProps> = ({ record, patientPhone, doctorFullName }) => {
  const docName = doctorFullName || (record as any).doctorName || "Docteur";
  const phone = patientPhone || (record as any).phone || "N/A";

  return (
    <StandardPDFLayout
      title="DOSSIER MÉDICAL"
      documentDate={record.dateCreation}
    >

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>INFORMATIONS PATIENT</Text>
        <View style={styles.row}>
          <Text style={styles.label}>Nom complet:</Text>
          <Text style={styles.value}>{record.patientFullName}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Téléphone:</Text>
          <Text style={styles.value}>{phone}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Date de création:</Text>
          <Text style={styles.value}>{record.dateCreation ? new Date(record.dateCreation).toLocaleDateString("fr-FR") : ""}</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>MÉDECIN</Text>
        <View style={styles.row}>
          <Text style={styles.label}>Nom du Médecin:</Text>
          <Text style={styles.value}>{docName}</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>SYMPTÔMES & ANTÉCÉDENTS</Text>
        <View style={styles.row}><Text style={styles.label}>Symptômes:</Text><Text style={styles.value}>{(record as any).symptomDuration || ""}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Durée:</Text><Text style={styles.value}>{record.symptoms}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Médicaments actuels:</Text><Text style={styles.value}>{record.currentMedication}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Conditions préexistantes:</Text><Text style={styles.value}>{formatMedicalConditions(record.preexistingCondition || "")}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Antécédents familiaux:</Text><Text style={styles.value}>{formatMedicalConditions(record.familyHistory || "")}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Allergies:</Text><Text style={styles.value}>{record.allergies}</Text></View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>ÉVALUATION</Text>
        <View style={styles.row}><Text style={styles.label}>Évaluation:</Text><Text style={styles.value}>{record.assessment}</Text></View>
        <View style={styles.row}><Text style={styles.label}>Recommandation:</Text><Text style={styles.value}>{record.recommendation}</Text></View>
      </View>

      {(record as any).gynecologyInfo && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>INFORMATION GYNÉCOLOGIQUE</Text>

          {/* Informations Générales */}
          <View style={styles.row}>
            <Text style={styles.label}>DDR:</Text>
            <Text style={styles.value}>{(record as any).gynecologyInfo.lastMenstrualDate ? new Date((record as any).gynecologyInfo.lastMenstrualDate).toLocaleDateString("fr-FR") : 'N/A'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Cycle:</Text>
            <Text style={styles.value}>{(record as any).gynecologyInfo.menstrualCycleLength ? `${(record as any).gynecologyInfo.menstrualCycleLength} jours` : 'N/A'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Historique:</Text>
            <Text style={styles.value}>
              G:{(record as any).gynecologyInfo.pregnancyCount || 0} /
              P:{(record as any).gynecologyInfo.childrenCount || 0} /
              A:{(record as any).gynecologyInfo.miscarriageCount || 0} /
              D:{(record as any).gynecologyInfo.deceasedChildrenCount || 0}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Contraception:</Text>
            <Text style={styles.value}>{(record as any).gynecologyInfo.currentContraceptiveMethod || 'Aucune'}</Text>
          </View>

          {/* Grossesse Actuelle (si applicable) */}
          {(record as any).gynecologyInfo.dateAccouchementPrevu && (
            <>
              <Text style={{ ...styles.label, width: '100%', marginTop: 4, marginBottom: 2, textDecoration: 'underline' }}>Grossesse actuelle:</Text>
              <View style={styles.row}>
                <Text style={styles.label}>DPA:</Text>
                <Text style={styles.value}>{new Date((record as any).gynecologyInfo.dateAccouchementPrevu).toLocaleDateString("fr-FR")}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Groupe Sanguin:</Text>
                <Text style={styles.value}>
                  {(record as any).gynecologyInfo.groupeSanguin || ''}
                  {(record as any).gynecologyInfo.rh || ''}
                </Text>
              </View>
            </>
          )}
        </View>
      )}

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>FICHIERS MÉDICAUX</Text>
        <View style={styles.box}>
          <Text style={styles.boxTitle}>Prescription (Ordonnance)</Text>
          <Text>- {record.currentMedication || "Non disponible"}</Text>

        </View>
        <View style={styles.box}>
          <Text style={styles.boxTitle}>Résultat (Analyses complémentaires)</Text>
          {Array.isArray((record as any).additionalExaminations) && (record as any).additionalExaminations.length > 0 ? (
            (record as any).additionalExaminations.map((ex: any, idx: number) => (
              <View key={idx} style={{ marginBottom: 4 }}>
                <Text>• {ex.examName}</Text>
                {ex.reason ? <Text style={styles.small}>Indication: {ex.reason}</Text> : null}
                {ex.concernedDiagnostics ? <Text style={styles.small}>Diagnostic(s): {ex.concernedDiagnostics}</Text> : null}
                {ex.patientAdvice ? <Text style={styles.small}>Conseils: {ex.patientAdvice}</Text> : null}
              </View>
            ))
          ) : (
            <Text>- Non disponible</Text>
          )}

        </View>
      </View>

    </StandardPDFLayout>
  );
};

const MedicalRecordPDF: React.FC<MedicalRecordPDFProps> = (props) => (
  <Document>
    <Page size="A5" style={styles.page}>
      <MedicalRecordContent {...props} />
    </Page>
  </Document>
);

export default MedicalRecordPDF;
